/**
 * The supported-locale registry — a typed, value-level module in the same shape
 * as the RBAC vocabulary registries in `app/utils/user.ts`: a frozen list of
 * codes, a type derived from that list, and the Default Language. The server
 * middleware, the client i18n init, and the dictionary-serving resource route
 * all read the locale set from here, so a clone edits exactly one place.
 *
 * Going monolingual is trimming {@link supportedLocales} to a single entry
 * (ADR-074): that alone narrows detection and the `Locale` type, after which the
 * orphaned dictionary folder can be deleted at leisure.
 *
 * This module intentionally carries NONE of viquell's URL-prefix machinery
 * (`useLang`/`useLocalizedPath`/`stripLangPrefix`, legacy-language redirects) —
 * ADR-074 keeps the UI locale in a cookie, never in the URL, so routing and
 * every existing `Link`/redirect stay untouched.
 */

/** The live, served UI locales. `de` leads: it is the Default Language. */
export const supportedLocales = ['de', 'en'] as const

export type Locale = (typeof supportedLocales)[number]

/**
 * The fallback when detection finds no supported locale — no cookie and an
 * absent or unsupported `Accept-Language`. This is the "German default" of
 * ADR-074; it must be a member of {@link supportedLocales}.
 */
export const DEFAULT_LOCALE: Locale = 'de'

/** The dedicated locale cookie's name — deliberately NOT the session cookie. */
export const LOCALE_COOKIE_NAME = 'lng'

/** Narrows an arbitrary string to a supported {@link Locale}. */
export function isSupportedLocale(value: string): value is Locale {
	return (supportedLocales as readonly string[]).includes(value)
}

/** Coerces an arbitrary string to a supported {@link Locale}, falling back to the default. */
export function toLocale(value: string): Locale {
	return isSupportedLocale(value) ? value : DEFAULT_LOCALE
}
